import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, Settings, Shield, BarChart, Target, AlertCircle, CheckCircle2, XCircle ,ShieldCheck} from 'lucide-react';
import { Button } from './ui/button';

const CookiePolicy = () => {
  const { language } = useLanguage();
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: false
  });

  const handleToggle = (type) => {
    if (type !== 'necessary') {
      setCookiePreferences(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    }
  };

  const savePreferences = () => {
    // Save preferences to localStorage or backend
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    alert(language === 'ar' ? 'تم حفظ التفضيلات بنجاح!' : 'Preferences saved successfully!');
  };

  const content = {
    en: {
      title: "Cookie Policy",
      lastUpdated: "Last Updated: January 2026",
      intro: "This Cookie Policy explains how Adidoo uses cookies and similar tracking technologies on our website. We respect your privacy and comply with Saudi Arabian data protection regulations, including the Personal Data Protection Law (PDPL).",
      whatAreCookies: {
        title: "What Are Cookies?",
        description: "Cookies are small text files that are placed on your device when you visit a website. They help websites remember your preferences, improve user experience, and provide analytics data. Cookies can be 'session' cookies (deleted when you close your browser) or 'persistent' cookies (remain on your device for a set period)."
      },
      sections: [
        {
          icon: Cookie,
          title: "1. Types of Cookies We Use",
          categories: [
            {
              name: "Necessary Cookies",
              required: true,
              description: "Essential for the website to function properly. These cannot be disabled.",
              examples: [
                "Authentication and security",
                "Shopping cart functionality",
                "Form submission",
                "Language preferences"
              ]
            },
            {
              name: "Functional Cookies",
              required: false,
              description: "Enable enhanced functionality and personalization.",
              examples: [
                "Remember your preferences",
                "Chatbot interactions",
                "Video player settings",
                "Regional settings"
              ]
            },
            {
              name: "Analytics Cookies",
              required: false,
              description: "Help us understand how visitors interact with our website.",
              examples: [
                "Google Analytics",
                "Page view tracking",
                "User journey analysis",
                "Performance monitoring"
              ]
            },
            {
              name: "Marketing Cookies",
              required: false,
              description: "Used to deliver relevant advertisements and track campaign effectiveness.",
              examples: [
                "Social media pixels",
                "Retargeting campaigns",
                "Conversion tracking",
                "Interest-based advertising"
              ]
            }
          ]
        },
        {
          icon: Settings,
          title: "2. How We Use Cookies",
          purposes: [
            "Authenticate users and prevent fraud",
            "Remember your preferences and settings",
            "Analyze website traffic and user behavior",
            "Improve website performance and functionality",
            "Deliver personalized content and advertisements",
            "Conduct A/B testing and optimization",
            "Comply with legal and regulatory requirements"
          ]
        },
        {
          icon: Shield,
          title: "3. Third-Party Cookies",
          description: "We may use third-party services that set their own cookies:",
          providers: [
            {
              name: "Google Analytics",
              purpose: "Website analytics and performance tracking",
              privacy: "https://policies.google.com/privacy"
            },
            {
              name: "Facebook Pixel",
              purpose: "Social media integration and advertising",
              privacy: "https://www.facebook.com/privacy"
            },
            {
              name: "LinkedIn Insights",
              purpose: "Professional network analytics",
              privacy: "https://www.linkedin.com/legal/privacy-policy"
            }
          ]
        },
        {
          icon: AlertCircle,
          title: "4. Your Cookie Choices",
          choices: [
            "Accept all cookies for the best experience",
            "Customize your cookie preferences using the settings below",
            "Disable cookies through your browser settings (may affect functionality)",
            "Use browser extensions to block tracking cookies",
            "Clear cookies periodically from your browser"
          ]
        },
        {
          icon: BarChart,
          title: "5. Cookie Duration",
          durations: [
            {
              type: "Session Cookies",
              duration: "Deleted when you close your browser",
              purpose: "Temporary functionality"
            },
            {
              type: "Persistent Cookies",
              duration: "Remain for days, months, or years",
              purpose: "Remember preferences and settings"
            },
            {
              type: "Analytics Cookies",
              duration: "Typically 1-2 years",
              purpose: "Long-term traffic analysis"
            }
          ]
        },
        {
          icon: Target,
          title: "6. Managing Cookies",
          instructions: [
            {
              browser: "Google Chrome",
              steps: "Settings > Privacy and Security > Cookies and other site data"
            },
            {
              browser: "Mozilla Firefox",
              steps: "Settings > Privacy & Security > Cookies and Site Data"
            },
            {
              browser: "Safari",
              steps: "Preferences > Privacy > Manage Website Data"
            },
            {
              browser: "Microsoft Edge",
              steps: "Settings > Privacy, Search, and Services > Cookies"
            }
          ]
        }
      ],
      compliance: {
        title: "Compliance with Saudi Regulations",
        description: "Our cookie practices comply with:",
        regulations: [
          "Personal Data Protection Law (PDPL)",
          "Saudi Data & AI Authority (SDAIA) guidelines",
          "E-Commerce Law requirements",
          "International best practices (GDPR alignment)"
        ]
      },
      preferences: {
        title: "Cookie Preferences",
        description: "Customize your cookie settings below:",
        save: "Save Preferences",
        saved: "Preferences Saved!"
      },
      contact: {
        title: "Questions About Cookies?",
        text: "If you have questions about our use of cookies:",
        email: "privacy@adidoo.com",
        phone: "+966 55 182 6405"
      }
    },
    ar: {
      title: "سياسة ملفات تعريف الارتباط",
      lastUpdated: "آخر تحديث: يناير 2026",
      intro: "توضح سياسة ملفات تعريف الارتباط هذه كيفية استخدام أديدو لملفات تعريف الارتباط وتقنيات التتبع المماثلة على موقعنا. نحن نحترم خصوصيتك ونمتثل للوائح حماية البيانات السعودية، بما في ذلك نظام حماية البيانات الشخصية.",
      whatAreCookies: {
        title: "ما هي ملفات تعريف الارتباط؟",
        description: "ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم وضعها على جهازك عند زيارة موقع ويب. تساعد المواقع على تذكر تفضيلاتك، وتحسين تجربة المستخدم، وتوفير بيانات التحليلات. يمكن أن تكون ملفات تعريف الارتباط 'جلسة' (يتم حذفها عند إغلاق المتصفح) أو 'دائمة' (تبقى على جهازك لفترة محددة)."
      },
      sections: [
        {
          icon: Cookie,
          title: "1. أنواع ملفات تعريف الارتباط التي نستخدمها",
          categories: [
            {
              name: "ملفات تعريف الارتباط الضرورية",
              required: true,
              description: "ضرورية لعمل الموقع بشكل صحيح. لا يمكن تعطيلها.",
              examples: [
                "المصادقة والأمان",
                "وظيفة عربة التسوق",
                "إرسال النماذج",
                "تفضيلات اللغة"
              ]
            },
            {
              name: "ملفات تعريف الارتباط الوظيفية",
              required: false,
              description: "تمكن الوظائف المحسنة والتخصيص.",
              examples: [
                "تذكر تفضيلاتك",
                "تفاعلات الدردشة",
                "إعدادات مشغل الفيديو",
                "الإعدادات الإقليمية"
              ]
            },
            {
              name: "ملفات تعريف الارتباط التحليلية",
              required: false,
              description: "تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.",
              examples: [
                "Google Analytics",
                "تتبع مشاهدات الصفحة",
                "تحليل رحلة المستخدم",
                "مراقبة الأداء"
              ]
            },
            {
              name: "ملفات تعريف الارتباط التسويقية",
              required: false,
              description: "تُستخدم لتقديم إعلانات ذات صلة وتتبع فعالية الحملات.",
              examples: [
                "بكسلات وسائل التواصل الاجتماعي",
                "حملات إعادة الاستهداف",
                "تتبع التحويل",
                "الإعلانات القائمة على الاهتمامات"
              ]
            }
          ]
        },
        {
          icon: Settings,
          title: "2. كيف نستخدم ملفات تعريف الارتباط",
          purposes: [
            "مصادقة المستخدمين ومنع الاحتيال",
            "تذكر تفضيلاتك وإعداداتك",
            "تحليل حركة المرور على الموقع وسلوك المستخدم",
            "تحسين أداء الموقع ووظائفه",
            "تقديم محتوى وإعلانات مخصصة",
            "إجراء اختبارات A/B والتحسين",
            "الامتثال للمتطلبات القانونية والتنظيمية"
          ]
        },
        {
          icon: Shield,
          title: "3. ملفات تعريف الارتباط من طرف ثالث",
          description: "قد نستخدم خدمات طرف ثالث تحدد ملفات تعريف الارتباط الخاصة بها:",
          providers: [
            {
              name: "Google Analytics",
              purpose: "تحليلات الموقع وتتبع الأداء",
              privacy: "https://policies.google.com/privacy"
            },
            {
              name: "Facebook Pixel",
              purpose: "دمج وسائل التواصل الاجتماعي والإعلان",
              privacy: "https://www.facebook.com/privacy"
            },
            {
              name: "LinkedIn Insights",
              purpose: "تحليلات الشبكة المهنية",
              privacy: "https://www.linkedin.com/legal/privacy-policy"
            }
          ]
        },
        {
          icon: AlertCircle,
          title: "4. خيارات ملفات تعريف الارتباط الخاصة بك",
          choices: [
            "قبول جميع ملفات تعريف الارتباط للحصول على أفضل تجربة",
            "تخصيص تفضيلات ملفات تعريف الارتباط باستخدام الإعدادات أدناه",
            "تعطيل ملفات تعريف الارتباط من خلال إعدادات المتصفح (قد يؤثر على الوظائف)",
            "استخدام ملحقات المتصفح لحظر ملفات تعريف الارتباط التتبعية",
            "مسح ملفات تعريف الارتباط بشكل دوري من متصفحك"
          ]
        },
        {
          icon: BarChart,
          title: "5. مدة ملفات تعريف الارتباط",
          durations: [
            {
              type: "ملفات تعريف ارتباط الجلسة",
              duration: "يتم حذفها عند إغلاق المتصفح",
              purpose: "وظائف مؤقتة"
            },
            {
              type: "ملفات تعريف الارتباط الدائمة",
              duration: "تبقى لأيام أو أشهر أو سنوات",
              purpose: "تذكر التفضيلات والإعدادات"
            },
            {
              type: "ملفات تعريف الارتباط التحليلية",
              duration: "عادة 1-2 سنة",
              purpose: "تحليل حركة المرور طويل الأجل"
            }
          ]
        },
        {
          icon: Target,
          title: "6. إدارة ملفات تعريف الارتباط",
          instructions: [
            {
              browser: "Google Chrome",
              steps: "الإعدادات > الخصوصية والأمان > ملفات تعريف الارتباط وبيانات الموقع الأخرى"
            },
            {
              browser: "Mozilla Firefox",
              steps: "الإعدادات > الخصوصية والأمان > ملفات تعريف الارتباط وبيانات الموقع"
            },
            {
              browser: "Safari",
              steps: "التفضيلات > الخصوصية > إدارة بيانات الموقع"
            },
            {
              browser: "Microsoft Edge",
              steps: "الإعدادات > الخصوصية والبحث والخدمات > ملفات تعريف الارتباط"
            }
          ]
        }
      ],
      compliance: {
        title: "الامتثال للوائح السعودية",
        description: "تتوافق ممارسات ملفات تعريف الارتباط لدينا مع:",
        regulations: [
          "نظام حماية البيانات الشخصية",
          "إرشادات الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)",
          "متطلبات نظام التجارة الإلكترونية",
          "أفضل الممارسات الدولية (توافق مع GDPR)"
        ]
      },
      preferences: {
        title: "تفضيلات ملفات تعريف الارتباط",
        description: "قم بتخصيص إعدادات ملفات تعريف الارتباط أدناه:",
        save: "حفظ التفضيلات",
        saved: "تم حفظ التفضيلات!"
      },
      contact: {
        title: "أسئلة حول ملفات تعريف الارتباط؟",
        text: "إذا كان لديك أسئلة حول استخدامنا لملفات تعريف الارتباط:",
        email: "privacy@adidoo.com",
        phone: "+966 55 182 6405"
      }
    }
  };

  const currentContent = content[language];

  const cookieTypes = [
    {
      id: 'necessary',
      icon: Shield,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'functional',
      icon: Settings,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'analytics',
      icon: BarChart,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'marketing',
      icon: Target,
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-50 to-red-50',
      borderColor: 'border-orange-200'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Cookie className="w-20 h-20 text-white mx-auto mb-6 animate-spin" style={{animationDuration: '3s'}} />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            {currentContent.title}
          </h1>
          <p className="text-purple-100 text-lg">{currentContent.lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-l-4 border-purple-600">
          <p className="text-gray-700 leading-relaxed text-lg" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.intro}
          </p>
        </div>

        {/* What Are Cookies */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 mb-8 border-2 border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <Cookie className="w-8 h-8 text-purple-600" />
            {currentContent.whatAreCookies.title}
          </h2>
          <p className="text-gray-700 leading-relaxed" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.whatAreCookies.description}
          </p>
        </div>

        {/* Cookie Preferences Interactive Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.preferences.title}
          </h2>
          <p className="text-gray-600 mb-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.preferences.description}
          </p>
          
          <div className="space-y-4">
            {currentContent.sections[0].categories.map((category, index) => {
              const cookieType = cookieTypes[index];
              const isEnabled = cookiePreferences[cookieType.id];
              
              return (
                <div key={index} className={`bg-gradient-to-br ${cookieType.bgColor} rounded-xl p-6 border-2 ${cookieType.borderColor}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 bg-gradient-to-br ${cookieType.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <cookieType.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-gray-900">{category.name}</h3>
                          {category.required && (
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                              {language === 'ar' ? 'مطلوب' : 'Required'}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm mb-3">{category.description}</p>
                        <ul className="space-y-1">
                          {category.examples.map((example, exIndex) => (
                            <li key={exIndex} className="text-xs text-gray-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggle(cookieType.id)}
                      disabled={true}
                      className={`flex-shrink-0 w-14 h-8 rounded-full transition-colors duration-300 ${
                        isEnabled ? 'bg-green-500' : 'bg-gray-300'
                      } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} relative`}
                    >
                      <div className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-transform duration-300 ${
                        isEnabled ? 'right-1' : 'left-1'
                      }`}></div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            onClick={savePreferences}
            className="w-full mt-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {currentContent.preferences.save}
          </Button>
        </div>

        {/* Other Sections */}
        {currentContent.sections.slice(1).map((section, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg p-8 mb-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <section.icon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 pt-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.title}
              </h2>
            </div>
            
            {section.purposes && (
              <ul className="space-y-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.purposes.map((purpose, pIndex) => (
                  <li key={pIndex} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{purpose}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.description && (
              <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
                <p className="text-gray-700 mb-4">{section.description}</p>
                {section.providers && (
                  <div className="space-y-3">
                    {section.providers.map((provider, pIndex) => (
                      <div key={pIndex} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-1">{provider.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{provider.purpose}</p>
                        <a href={provider.privacy} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 hover:underline">
                          {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'} →
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {section.choices && (
              <ul className="space-y-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.choices.map((choice, cIndex) => (
                  <li key={cIndex} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">{choice}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.durations && (
              <div className="space-y-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.durations.map((duration, dIndex) => (
                  <div key={dIndex} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{duration.type}</h4>
                    <p className="text-sm text-gray-600 mb-1"><strong>{language === 'ar' ? 'المدة:' : 'Duration:'}</strong> {duration.duration}</p>
                    <p className="text-sm text-gray-600"><strong>{language === 'ar' ? 'الغرض:' : 'Purpose:'}</strong> {duration.purpose}</p>
                  </div>
                ))}
              </div>
            )}

            {section.instructions && (
              <div className="space-y-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                {section.instructions.map((instruction, iIndex) => (
                  <div key={iIndex} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{instruction.browser}</h4>
                    <p className="text-sm text-gray-600">{instruction.steps}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Compliance */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 mb-8 border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <ShieldCheck className="w-8 h-8 text-green-600" />
            {currentContent.compliance.title}
          </h2>
          <p className="text-gray-700 mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.compliance.description}
          </p>
          <ul className="space-y-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.compliance.regulations.map((regulation, index) => (
              <li key={index} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-gray-800 font-medium">{regulation}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.title}
          </h2>
          <p className="mb-6 text-purple-100" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {currentContent.contact.text}
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5" />
              <a href={`mailto:${currentContent.contact.email}`} className="hover:underline" dir="ltr">
                {currentContent.contact.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5" />
              <a href={`tel:${currentContent.contact.phone}`} className="hover:underline" dir="ltr">
                {currentContent.contact.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;